import type { ReactNode } from 'react';
import { useGetLocale, useList, useTranslate } from '@refinedev/core';
import {
  Card,
  Descriptions,
  Grid,
  Image,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import { RollbackOutlined, StopOutlined } from '@ant-design/icons';
import type {
  ModStatus,
  PublicationCollectionOut,
  PublicationDetailOut,
  ThemaCodeOut,
} from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { StatusDot } from '@/components/common/StatusDot';
import { statusTone } from '@/components/common/statusTone';
import { formatDate, formatDateTime } from '@/utils/format';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { EXCLUSION_FAMILIES } from '../excludedIds';
import type { ExclusionFamily } from '../excludedIds';
import { useExclusionToggle } from './useExclusionToggle';

const { Text, Paragraph } = Typography;

interface PublicationGeneralTabProps {
  record: PublicationDetailOut;
}

const FAMILY_UI: Record<
  ExclusionFamily['key'],
  { resource: string; color: string | undefined; labelKey: string }
> = {
  categories: {
    resource: 'user_catalog.categories',
    color: 'blue',
    labelKey: 'user_catalog.publications.fields.categories',
  },
  collections: {
    resource: 'user_catalog.managed_collections',
    color: 'purple',
    labelKey: 'user_catalog.publications.fields.collections',
  },
  tags: {
    resource: 'user_catalog.tags',
    color: undefined,
    labelKey: 'user_catalog.publications.show.classification.custom_tags',
  },
};

const nameMap = (items: PublicationCollectionOut[] | undefined) =>
  new Map((items ?? []).map((item) => [item.id, item.name ?? `#${item.id}`]));

const PositionBadge = ({ position }: { position: number }) => {
  const { token } = theme.useToken();
  return (
    <span
      style={{
        width: 20,
        height: 20,
        flex: 'none',
        borderRadius: '50%',
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
        fontSize: token.fontSizeSM,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: token.colorTextTertiary,
      }}
    >
      {position}
    </span>
  );
};

export const PublicationGeneralTab = ({
  record,
}: PublicationGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();
  const locale = useGetLocale()();
  const screens = Grid.useBreakpoint();
  const { exclude, restore, isPending } = useExclusionToggle(record);

  const collectionNames = nameMap(record.collections);

  // Thema descriptions for the tooltip — one batched lookup by code.
  const themaCodes = record.thema_codes ?? [];
  const { result: themaResult } = useList<ThemaCodeOut>({
    resource: 'content_catalog.thema_codes',
    filters: [{ field: 'code', operator: 'in', value: themaCodes }],
    pagination: { pageSize: Math.max(themaCodes.length, 1) },
    queryOptions: { enabled: themaCodes.length > 0 },
  });
  const themaDescriptions = new Map(
    (themaResult.data ?? []).map((item) => {
      const desc = item.description ?? {};
      const text =
        (locale === 'en' ? desc.en || desc.pl : desc.pl || desc.en) ?? null;
      return [item.code, text];
    })
  );

  const languageDisplay = (code: string) => {
    try {
      const name = new Intl.DisplayNames([locale ?? 'pl'], {
        type: 'language',
      }).of(code);
      return name && name.toLowerCase() !== code.toLowerCase()
        ? `${name} (${code})`
        : code;
    } catch {
      return code;
    }
  };

  const idHint = (id: number) => (
    <Text type="secondary">
      {' '}
      {t('user_catalog.publications.show.fields.id_hint', { id })}
    </Text>
  );

  const yesNo = (value: boolean) =>
    value ? (
      <Text style={{ color: token.colorSuccessText }}>{t('common.yes')}</Text>
    ) : (
      <Text type="secondary">{t('common.no')}</Text>
    );

  const sourceCode = (sourceId: number | null | undefined) =>
    sourceId != null && (
      <ShowLink resource="content_catalog.products" id={sourceId}>
        <Text code>{sourceId}</Text>
      </ShowLink>
    );

  const generalItems: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: record.id,
    },
    {
      key: 'slug',
      label: t('user_catalog.publications.fields.slug'),
      children: <Text code>{record.slug}</Text>,
    },
    {
      key: 'version',
      label: t('user_catalog.publications.fields.version'),
      children: orDash(record.version),
    },
    {
      key: 'title',
      label: t('user_catalog.publications.fields.title'),
      children: record.title,
    },
    {
      key: 'subtitle',
      label: t('user_catalog.publications.fields.subtitle'),
      children: orDash(record.subtitle),
      span: 2,
    },
    {
      key: 'publisher',
      label: t('user_catalog.publications.fields.publisher'),
      children: (
        <>
          <ShowLink resource="user_catalog.publishers" id={record.publisher_id}>
            {record.publisher?.name ?? `#${record.publisher_id}`}
          </ShowLink>
          {idHint(record.publisher_id)}
        </>
      ),
    },
    {
      key: 'main_series',
      label: t('user_catalog.publications.fields.main_series'),
      children: record.main_series_id
        ? ((
            <>
              <ShowLink
                resource="user_catalog.managed_collections"
                id={record.main_series_id}
              >
                {collectionNames.get(record.main_series_id) ??
                  `#${record.main_series_id}`}
              </ShowLink>
              {idHint(record.main_series_id)}
            </>
          ) as ReactNode)
        : orDash(null),
    },
    {
      key: 'language',
      label: t('user_catalog.publications.fields.language'),
      children: orDash(record.language && languageDisplay(record.language)),
    },
    {
      key: 'audience',
      label: t('user_catalog.publications.fields.audience'),
      children: t(`user_catalog.publications.audience.${record.audience}`),
    },
    {
      key: 'duration',
      label: t('user_catalog.publications.show.fields.duration'),
      children: t(`user_catalog.publications.duration.${record.duration}`),
    },
    {
      key: 'exclusive_premiere',
      label: t('user_catalog.publications.show.fields.exclusive_premiere'),
      children: (
        <>
          {yesNo(record.is_exclusive)} / {yesNo(record.is_premiere)}
        </>
      ),
    },
    {
      key: 'published_at',
      label: t('user_catalog.publications.fields.published_at'),
      children: orDash(formatDate(record.published_at)),
    },
    {
      key: 'first_available_from',
      label: t('user_catalog.publications.show.fields.first_available'),
      children: orDash(formatDate(record.first_available_from)),
    },
    {
      key: 'sources',
      label: t('user_catalog.publications.show.fields.sources'),
      children:
        record.ebook_source_id == null && record.audio_source_id == null ? (
          orDash(null)
        ) : (
          <Space size={6}>
            {sourceCode(record.ebook_source_id)}
            {sourceCode(record.audio_source_id)}
          </Space>
        ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  const creatorsSection = (
    title: string,
    items: {
      id: number;
      personId: number;
      position: number;
      name?: string;
      modStatus?: ModStatus;
    }[],
    resource: string
  ) => (
    <div>
      <div
        style={{
          fontSize: token.fontSizeSM,
          color: token.colorTextTertiary,
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      {items.length === 0 ? (
        orDash(null)
      ) : (
        <Space direction="vertical" size={6} style={{ display: 'flex' }}>
          {items
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((item) => (
              <Space key={item.id} size={8}>
                <PositionBadge position={item.position} />
                <ShowLink resource={resource} id={item.personId}>
                  {item.name ?? `#${item.personId}`}
                </ShowLink>
                {item.modStatus && (
                  <StatusDot
                    tone={statusTone(item.modStatus)}
                    title={`${t('base.fields.mod_status')}: ${t(
                      `base.mod_status.${item.modStatus}`
                    )}`}
                  />
                )}
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  ID {item.personId}
                </Text>
              </Space>
            ))}
        </Space>
      )}
    </div>
  );

  const membershipTags = (family: ExclusionFamily) => {
    const ui = FAMILY_UI[family.key];
    const names = nameMap(record[family.membersField]);
    const activeIds = [
      ...(record[family.staticField] ?? []),
      ...(record[family.dynamicField] ?? []),
    ];
    const staticSet = new Set(record[family.staticField] ?? []);
    const excludedIds = record[family.excludedField] ?? [];
    const iconStyle = {
      fontSize: token.fontSizeSM,
      cursor: isPending ? 'not-allowed' : 'pointer',
      opacity: isPending ? 0.4 : 1,
    };

    return (
      <Space direction="vertical" size={6} style={{ display: 'flex' }}>
        {activeIds.length === 0 ? (
          orDash(null)
        ) : (
          <Space size={[6, 6]} wrap>
            {activeIds.map((id) => {
              const isDynamic = !staticSet.has(id);
              return (
                <Tag
                  key={id}
                  color={ui.color}
                  closable
                  closeIcon={
                    <Tooltip
                      title={t(
                        'user_catalog.publications.show.classification.exclude'
                      )}
                    >
                      <StopOutlined style={iconStyle} />
                    </Tooltip>
                  }
                  onClose={(e) => {
                    e.preventDefault();
                    if (!isPending) exclude(family.excludedField, id);
                  }}
                  style={{
                    marginInlineEnd: 0,
                    ...(isDynamic ? { borderStyle: 'dashed' } : {}),
                  }}
                >
                  <ShowLink resource={ui.resource} id={id}>
                    {names.get(id) ?? `#${id}`}
                  </ShowLink>
                  {isDynamic
                    ? ` ${t('user_catalog.publications.show.classification.dynamic_suffix')}`
                    : ''}
                </Tag>
              );
            })}
          </Space>
        )}
        {excludedIds.length > 0 && (
          <div>
            <Text
              type="secondary"
              style={{ fontSize: token.fontSizeSM, marginRight: 6 }}
            >
              {t(
                'user_catalog.publications.show.classification.excluded_label'
              )}
              :
            </Text>
            <Space size={[6, 6]} wrap>
              {excludedIds.map((id) => (
                <Tag
                  key={id}
                  closable
                  closeIcon={
                    <Tooltip
                      title={t(
                        'user_catalog.publications.show.classification.restore'
                      )}
                    >
                      <RollbackOutlined style={iconStyle} />
                    </Tooltip>
                  }
                  onClose={(e) => {
                    e.preventDefault();
                    if (!isPending) restore(family.excludedField, id);
                  }}
                  style={{ marginInlineEnd: 0, color: token.colorTextTertiary }}
                >
                  <ShowLink resource={ui.resource} id={id}>
                    <span
                      style={{
                        color: token.colorTextTertiary,
                        textDecoration: 'line-through',
                      }}
                    >
                      {names.get(id) ?? `#${id}`}
                    </span>
                  </ShowLink>
                </Tag>
              ))}
            </Space>
          </div>
        )}
      </Space>
    );
  };

  const classificationGroup = (label: string, children: ReactNode) => (
    <div>
      <div
        style={{
          fontSize: token.fontSizeSM,
          color: token.colorTextTertiary,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: screens.lg ? '1fr 380px' : '1fr',
        gap: token.margin,
        alignItems: 'start',
      }}
    >
      <Space
        direction="vertical"
        size={token.margin}
        style={{ display: 'flex' }}
      >
        <Card title={t('user_catalog.publications.show.sections.general_info')}>
          <Descriptions
            {...verticalDescProps}
            column={{ xs: 1, sm: 2, md: 3 }}
            items={generalItems}
          />
        </Card>
        <Card title={t('user_catalog.publications.show.sections.description')}>
          {record.description ? (
            <Paragraph style={{ marginBottom: 0 }}>
              {record.description}
            </Paragraph>
          ) : (
            orDash(null)
          )}
        </Card>
      </Space>

      <Space
        direction="vertical"
        size={token.margin}
        style={{ display: 'flex' }}
      >
        <Card title={t('user_catalog.publications.show.sections.creators')}>
          <Space
            direction="vertical"
            size={token.marginSM}
            style={{ display: 'flex' }}
          >
            {creatorsSection(
              t('user_catalog.publications.authors.title'),
              (record.publication_authors ?? []).map((a) => ({
                id: a.id,
                personId: a.author_id,
                position: a.position,
                name: a.author?.name,
                modStatus: a.author?.mod_status,
              })),
              'user_catalog.authors'
            )}
            {creatorsSection(
              t('user_catalog.publications.lectors.title'),
              (record.publication_lectors ?? []).map((l) => ({
                id: l.id,
                personId: l.lector_id,
                position: l.position,
                name: l.lector?.name,
                modStatus: l.lector?.mod_status,
              })),
              'user_catalog.lectors'
            )}
          </Space>
        </Card>

        <Card
          title={t('user_catalog.publications.show.sections.classification')}
        >
          <Space direction="vertical" size={14} style={{ display: 'flex' }}>
            {classificationGroup(
              t('user_catalog.publications.fields.thema_codes'),
              record.thema_codes?.length ? (
                <Space size={[6, 6]} wrap>
                  {record.thema_codes.map((code) => (
                    <Tooltip key={code} title={themaDescriptions.get(code)}>
                      <Tag style={{ marginInlineEnd: 0 }}>{code}</Tag>
                    </Tooltip>
                  ))}
                </Space>
              ) : (
                orDash(null)
              )
            )}
            {classificationGroup(
              t('user_catalog.publications.fields.warnings'),
              record.warnings.length ? (
                <Space size={[6, 6]} wrap>
                  {record.warnings.map((warning) => (
                    <Tag
                      key={warning}
                      color="gold"
                      style={{ marginInlineEnd: 0 }}
                    >
                      {t(
                        `user_catalog.publications.warnings.${
                          { 1: 'profanity', 2: 'violence', 4: 'adult_content' }[
                            warning
                          ]
                        }`
                      )}
                    </Tag>
                  ))}
                </Space>
              ) : (
                orDash(null)
              )
            )}
            {EXCLUSION_FAMILIES.map((family) => (
              <div key={family.key}>
                {classificationGroup(
                  t(FAMILY_UI[family.key].labelKey),
                  membershipTags(family)
                )}
              </div>
            ))}
            <Text
              type="secondary"
              style={{
                fontSize: token.fontSizeSM,
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                paddingTop: 10,
              }}
            >
              {t('user_catalog.publications.show.classification.dynamic_hint')}
              {' · '}
              {t('user_catalog.publications.show.classification.excluded_hint')}
            </Text>
          </Space>
        </Card>

        <Card title={t('user_catalog.publications.show.sections.covers')}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '96px 1fr',
              gap: token.margin,
              alignItems: 'start',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextTertiary,
                  marginBottom: 6,
                }}
              >
                cover_url
              </div>
              <CoverThumb url={record.cover_url} size={96} />
            </div>
            <div>
              <div
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextTertiary,
                  marginBottom: 6,
                }}
              >
                large_cover_url{' '}
                <Text
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM, opacity: 0.6 }}
                >
                  {t('user_catalog.publications.show.fields.optional_suffix')}
                </Text>
              </div>
              {record.large_cover_url ? (
                <Image
                  src={record.large_cover_url}
                  width="100%"
                  style={{
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                    borderRadius: token.borderRadius,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    borderRadius: token.borderRadius,
                    border: `1px dashed ${token.colorBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {t('user_catalog.publications.show.fields.no_large_cover')}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Card>
      </Space>
    </div>
  );
};
